import React, { useEffect, useRef, useState } from 'react';
import { PauseCircleFilled, PlayCircleFilled, FullscreenOutlined, UndoOutlined } from '@ant-design/icons';
import { convertSecondsToTime } from '../utils/videoTimer';
import { useNuStreamerContext } from '../NuStreamerContext';
import { getBackgroundImg, getUploadedImagePath, isFunction, useBreakpoints } from '@nutastic/nutastic-react-app';
import useWindowSize from '../../../hooks/useWindowSize';
import classnames from 'classnames';
import NuStreamerVideo from './NuStreamerVideo';

const componentClass = 'nuVideoPlayer';

const VideoPlayer = ({ videoId, videoSrc, videoConfig, image, widthOverride, heightOverride, videoClassName, startTime }) => {
    const {
        isPlayingVideo,
        timeProgress,
        setTimeProgress,
        duration,
        showCompactVideo,
        setShowCompactVideo,
        activeVideo,
        stopVideoPlayer,
        startVideoPlayer,
        onUpdateMediaSession,
        STREAMER_MODES,
        setStreamerMode,
        onLoadBroadcast,
        setIsSeeking,
        setVideoList
    } = useNuStreamerContext();
    const videoRef = useRef(null);
    const { xl: isXtraLargeScreen } = useBreakpoints();
    const { windowWidth } = useWindowSize();
    const isPlayingInMiniPlayer = showCompactVideo && isPlayingVideo;
    const thisVideoIsActive = (activeVideo?.videoId || activeVideo?.config?.id) === (videoId || videoConfig?.id);
    const thisVideoIsPlaying = isPlayingVideo && thisVideoIsActive;
    const [hasStarted, setHasStarted] = useState(thisVideoIsPlaying);
    const isBroadcastMode = (videoConfig || {}).hasOwnProperty('broadcastId') && !!videoConfig.broadcastId
        && (videoConfig || {}).hasOwnProperty('broadcastData') && !!videoConfig.broadcastData
        && (videoConfig || {}).hasOwnProperty('onLoadBroadcast') && isFunction(videoConfig.onLoadBroadcast);
    const isPlaylistMode = (videoConfig || {}).hasOwnProperty('videoListId')
        && (videoConfig || {}).hasOwnProperty('videoList');

    const videoWidth = isXtraLargeScreen ? 1280 : windowWidth;
    const videoHeight = isXtraLargeScreen ? 720 : (videoWidth / (16/9));
    const pixelOffset = isXtraLargeScreen ? 4 : 0;

    const finalWidth = (widthOverride || videoWidth)-pixelOffset;
    const finalHeight = heightOverride || videoHeight;

    const videoControlClasses = classnames(`${componentClass}__videoControls`, {
        isNotPlaying: !thisVideoIsActive
    });

    //Start playing the video is the current compact video is closed and the video loaded is the same one
    useEffect(() => {
        if (!showCompactVideo && videoRef && videoRef.current && thisVideoIsPlaying) {
            videoRef.current.someoneIsSeeking = true;
            videoRef.current.currentTime = timeProgress;
            onUpdateMediaSession();
            videoRef.current.play();
        }
    }, [showCompactVideo]);

    const onFullscreenClick = () => {
        if (videoRef && videoRef.current) {
            const vid = videoRef.current;
            if (vid?.requestFullscreen) {
                vid.requestFullscreen();
            } else if (vid?.webkitRequestFullScreen) {
                vid.webkitRequestFullScreen();
            } else if (vid?.mozRequestFullScreen) {
                vid.mozRequestFullScreen();
            } else if (vid?.msRequestFullscreen) {
                vid.msRequestFullscreen();
            } else if (vid?.webkitEnterFullscreen) {
                vid.webkitEnterFullscreen();
            }
        }
    };

    const handleSeekMouseDown = () => {
        setIsSeeking(true);
    };

    const handleSeekMouseUp = e => {
        setIsSeeking(false);
        if(videoRef && videoRef.current) {
            videoRef.current.someoneIsSeeking = true;
            videoRef.current.currentTime = parseFloat(e.target.value);
        }
    };

    const handleSeekChange = e => {
        setTimeProgress(parseFloat(e.target.value));
    };

    const onCloseCompactPlayer = () => {
        if (thisVideoIsPlaying) {
            setShowCompactVideo(false);
        } else {
            stopVideoPlayer();
        }
    };

    const theyReallyWannaPlay = (shouldStart, ignoreStartTime) => {
        if(videoRef.current) {
            if (isBroadcastMode) {
                setStreamerMode(STREAMER_MODES.BROADCAST);
                onLoadBroadcast(videoConfig.broadcastId, videoRef);
            } else {
                setStreamerMode(isPlaylistMode ? STREAMER_MODES.PLAYLIST : STREAMER_MODES.VIDEO);
                if(isPlaylistMode) setVideoList(videoConfig.videoList);
                if(shouldStart) startVideoPlayer(videoId, videoSrc, videoConfig);
                if(!ignoreStartTime && !hasStarted && startTime) {
                    videoRef.current.currentTime = startTime;
                }
                videoRef?.current?.play();
                setHasStarted(true);
            }
        }
    };

    const onPressPlay = (ignoreStartTime) => {
        if(!isPlayingVideo || !thisVideoIsPlaying) {
            if(!thisVideoIsPlaying) {
                stopVideoPlayer();
            }
            theyReallyWannaPlay(true, ignoreStartTime);
        } else {
            const videoIsPaused = videoRef?.current?.paused;
            if (videoIsPaused === undefined) {
                console.error('Attempting to check video pause state, but no ref');
            } else if(videoIsPaused) {
                theyReallyWannaPlay();
            } else if(!isBroadcastMode) {
                videoRef?.current?.pause();
            }
        }
    };

    const onStopBroadcasting = () => {
        videoRef.isStoppingBroadcast = true;
        videoRef.current.pause();
        stopVideoPlayer();
    };

    const overlayControlsRender = <div
        className={`${componentClass}__overlayControls`}
        onClick={() => onPressPlay(false)}
        style={{ width: finalWidth, height: finalHeight }}
    >
        { !isPlayingVideo && <i className="fa fa-play-circle-o" /> }
    </div>;

    return <div className={componentClass}>
        { isPlayingInMiniPlayer && <div
            className={`${componentClass}__compactPlayerCover`}
            onClick={onCloseCompactPlayer}
            style={{ width: finalWidth, height: finalHeight }}
        >
            <div style={getBackgroundImg(getUploadedImagePath(image))} />
            <span>A video is playing in the compact player, click here to use main video player</span>
        </div> }
        { !isPlayingInMiniPlayer && overlayControlsRender }
        { !isPlayingInMiniPlayer &&
            <NuStreamerVideo
                width={ finalWidth }
                height={ finalHeight }
                playsInline={true}
                usePip={false}
                elementRef={videoRef}
                image={getUploadedImagePath(image)}
                className={videoClassName}
            >
                <source src={activeVideo?.videoSource || videoSrc} />
            </NuStreamerVideo>
        }
        { (!isPlayingInMiniPlayer && !isBroadcastMode) && <div className={videoControlClasses} style={{ width:finalWidth }}>
            <button onClick={() => onPressPlay(false)}>
                { thisVideoIsPlaying ? <PauseCircleFilled /> : <PlayCircleFilled /> }
            </button>
            { (!thisVideoIsActive && startTime > 0) && <button onClick={() => onPressPlay(true)}><UndoOutlined /></button> }
            { thisVideoIsActive && <div className="videoProgress">
                { (timeProgress && timeProgress > 0) ? <span>{ convertSecondsToTime(timeProgress) }</span> : <span /> }
                <input
                    type='range'
                    min={0}
                    max={duration}
                    step='any'
                    value={timeProgress || 0}
                    onMouseDown={handleSeekMouseDown}
                    onChange={handleSeekChange}
                    onMouseUp={handleSeekMouseUp}
                    className="videoProgress__slider"
                />
                { (duration && duration > 0) ? <span>{ convertSecondsToTime(duration) }</span> : <span /> }
            </div> }
            { thisVideoIsActive && <button onClick={onFullscreenClick}>
                <FullscreenOutlined />
            </button> }
        </div> }
        { (isBroadcastMode && thisVideoIsPlaying) && <div className="channelController">
            <button onClick={onStopBroadcasting}><i className="fa fa-stop-circle" /></button>
            <button onClick={onFullscreenClick}><i className="fa fa-arrows-alt" /></button>
            <span><b>Now Playing : </b> {`${activeVideo?.config?.streamTitle || activeVideo?.config?.title}`}</span>
        </div> }
    </div>;
}

export default VideoPlayer;
